import { useState, useEffect, useCallback } from 'react'
import { db, type TopicoProgresso } from '@services/db.js'
import { toggleFavorito as dbToggleFavorito } from '@services/crud.js'
import modulosData from '@content/modulos.json'
import { MODULO_TOTAL_TOPICOS, type Modulo } from '@utils/constants.js'

export type TopicoStatus = 'nao-iniciado' | 'em-andamento' | 'concluido'

export interface TopicoComStatus {
  id: string
  slug: string
  titulo: string
  ordem: number
  moduloId: string
  status: TopicoStatus
  favorito: boolean
  progresso: TopicoProgresso | null
}

export interface ModuloDetalhe extends Modulo {
  concluidos: number
  total: number
  pct: number
  topicos: TopicoComStatus[]
}

function getStatus(prog: TopicoProgresso | null): TopicoStatus {
  if (!prog) return 'nao-iniciado'
  if (prog.concluido) return 'concluido'
  const temProgresso = prog.percentualPratico > 0 || prog.checklistItems.some((c) => c.concluido)
  return temProgresso ? 'em-andamento' : 'nao-iniciado'
}

export function useModuloDetail(moduloId: string) {
  const [modulo, setModulo] = useState<ModuloDetalhe | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async () => {
    const moduloJson = modulosData.modulos.find((m) => m.id === moduloId)
    if (!moduloJson) {
      setIsLoading(false)
      return
    }

    const progressoList = await db.topicos_progresso.where('moduloId').equals(moduloId).toArray()

    const progressoMap = new Map(progressoList.map((p) => [p.topicoId, p]))

    const topicos: TopicoComStatus[] = moduloJson.topicos.map((t) => {
      const prog = progressoMap.get(t.id) ?? null
      return {
        id: t.id,
        slug: t.slug,
        titulo: t.titulo,
        ordem: t.ordem,
        moduloId,
        status: getStatus(prog),
        favorito: prog?.favorito ?? false,
        progresso: prog,
      }
    })

    const concluidos = topicos.filter((t) => t.status === 'concluido').length
    const total = MODULO_TOTAL_TOPICOS[moduloId] ?? topicos.length
    const pct = total > 0 ? Math.round((concluidos / total) * 100) : 0

    setModulo({
      id: moduloJson.id,
      slug: moduloJson.slug,
      titulo: moduloJson.titulo,
      icone: moduloJson.icone,
      ordem: moduloJson.ordem,
      concluidos,
      total,
      pct,
      topicos,
    })
    setIsLoading(false)
  }, [moduloId])

  useEffect(() => {
    load()
  }, [load])

  const toggleFavorito = useCallback(
    async (topicoId: string) => {
      await dbToggleFavorito(topicoId, moduloId)
      await load()
    },
    [moduloId, load],
  )

  return { modulo, isLoading, reload: load, toggleFavorito }
}
